import { apiProvider as reportApiProvider } from '@/modules/sestavy/ReportRequest/api';

import { NAME as ReportRequestName, SEND_REQUEST, SEARCH } from '@/modules/sestavy/ReportRequest/constants';

import { UserRole } from '../components/constants';
import { openEdit, openList } from '../router/routerPath';
import { PageMode } from './pageMode';
import { Row } from './table';
import { showAlert } from './notifications';
import { LANGUAGE_CODE, LANGUAGE_ID } from '../store/constants';

export function isUserPartsManager(userRoleCode: string | null, userRoles?: string[] | null): boolean {
	let isPartsManager = false;
	if (userRoles && userRoles.length) {
		isPartsManager = userRoles.map((x: any) => x.UserRoleType).includes(UserRole.PARTS_MANAGER);
	}
	return (userRoleCode === UserRole.PARTS_MANAGER || isPartsManager);
}

export function OpenPartMaintenance({ cellNames, row }: { cellNames: any, row: Row }): void {
	const partIdIndex = cellNames?.PART_STOCK_ID!;
	const partId = row.cells[partIdIndex];
	if (partId) openEdit('NdSklad', PageMode.EDIT, { id: partId, defaultAction: true });
}

export async function createReportRequest(
	moduleName: string,
	requestData: Record<string, any>,
	componentContext: any,
): Promise<boolean> {
	try {
		const { data: reportData } = await reportApiProvider.CreateReportRequest(moduleName, requestData);
		if (reportData) {
			if (reportData.IsSuccess) {
				componentContext.showConfirm({
					message: componentContext.LocalizeChyboveHlasky(reportData.Message),
					title: componentContext.LocalizeChyboveHlasky('DMS3_InfoTitle'),
					options: {
						type: 'info',
						confirmButtonText: componentContext.Localize('CheckStatus'),
						cancelButtonText: componentContext.Localize('Close'),
						showCancelButton: true,
						closeOnClickModal: false,
						closeOnPressEscape: false,
						callback: async (action: string) => {
							if (action === 'confirm') {
								openList(ReportRequestName);
							}
						},
					},
				});
			} else {
				showAlert({
					message: componentContext.LocalizeChyboveHlasky(reportData.Message),
					title: componentContext.LocalizeChyboveHlasky('DMS3_ErrorTitle'),
					options: {
						type: 'error',
					},
				});
				return false;
			}
		} else {
			showAlert({
				message: componentContext.LocalizeChyboveHlasky('LocalMsg'),
				title: componentContext.LocalizeChyboveHlasky('DMS3_ErrorTitle'),
				options: {
					type: 'error',
				},
			});
			return false;
		}
	} catch (error) {
		showAlert({
			message: componentContext.LocalizeChyboveHlasky('LocalMsg'),
			title: componentContext.LocalizeChyboveHlasky('DMS3_ErrorTitle'),
			options: {
				type: 'error',
			},
		});
		return false;
	}
	return true;
}

export function isMultipleWorkshopSelected(workshopIds: any[]): boolean {
	return workshopIds && workshopIds.length > 1;
}

export function getSearchButtonLabel(workshopIds: any[]): string {
	return isMultipleWorkshopSelected(workshopIds) ? SEND_REQUEST : SEARCH;
}

export async function handleMultiWorkshopFilter(
	component: any,
	reportName: string,
	workshopIds: any[],
	buildRequestParams: (columnsSeq: string[]) => any,
): Promise<void> {
	if (isMultipleWorkshopSelected(workshopIds)) {
		try {
			component.startLoading();
			const columnsSeq = (component as any).table.columns.filter((x: any) => x.isVisible === true).map((x: any) => x.columnName);
			const requestParams = buildRequestParams(columnsSeq);
			await createReportRequest(reportName, requestParams, component);
			component.table.rows = [];
		} finally {
			component.endLoading();
		}
	} else {
		if (workshopIds == null || workshopIds.length === 0) {
			component.showErrorAlert({ message: component.LocalizePopisPoli('DEALER_CODE_NOT_NULL') });
			return;
		}
		await component.tableData();
	}
}

export function navigateToReportRequests(): void {
	openList(ReportRequestName);
}

/**
 * Generic helper to calculate the count from a dependent table.
 * If the table has loaded rows & columns, returns rows.length.
 * Otherwise returns the fallback count (typically from the edit object).
 *
 * @param table - The dependent table object (e.g. dependentTables.ZakazkaNd)
 * @param fallbackCount - The fallback count from the edit/store (e.g. this.PartsCount)
 * @returns The calculated count
 */
export function getTableCount(table: { rows?: any[]; columns?: any[] } | undefined | null, fallbackCount: number | null | undefined): number {
	if (table?.rows && table.rows.length >= 0 && table?.columns && table.columns.length > 0) {
		return table.rows.length;
	}
	return fallbackCount ?? 0;
}

export function validateDateFields(context: any, dateFromProperty: string, dateToProperty: string): boolean {
	const dateFrom = context[dateFromProperty];
	const dateTo = context[dateToProperty];

	if (!dateFrom) {
		context.showErrorAlert({ message: context.LocalizePopisPoli('DP_DATEFROM_VALIDATION') });
		return false;
	}
	if (!dateTo) {
		context.showErrorAlert({ message: context.LocalizePopisPoli('DP_DATETO_VALIDATION') });
		return false;
	}

	return true;
}

export function getCurrentLanguageId(defaultLanguageId: number | null = LANGUAGE_ID.ENGLISH): number | null {
	const languageCode = localStorage.getItem('i18nextLng');
	let languageId = null;

	if (languageCode === LANGUAGE_CODE.CZECH) {
		languageId = LANGUAGE_ID.CZECH;
	} else if (languageCode === LANGUAGE_CODE.MANDARIN) {
		languageId = LANGUAGE_ID.MANDARIN;
	} else if (languageCode === LANGUAGE_CODE.ENGLISH) {
		languageId = LANGUAGE_ID.ENGLISH;
	} else {
		languageId = defaultLanguageId;
	}
	return languageId;
}
